<template>
    <div>
        <limitless-page-header></limitless-page-header>
        <div class="content">
            <b-card>
                <b-card-header>
                    <b-button-group>
                        <b-button :variant="filter.serviceType === '' ? 'primary' : 'outline-primary'" @click="setFilter('serviceType', '')">Visa alla</b-button>
                        <b-button v-for="serviceType in serviceTypes"
                                  :variant="filter.serviceType === serviceType ? 'primary' : 'outline-primary'"
                                  :key="serviceType"
                                  @click="setFilter('serviceType', serviceType)">
                            {{ serviceType }}
                        </b-button>
                    </b-button-group>
                    <b-button-group>
                        <b-button :variant="filter.countryCode === '' ? 'primary' : 'outline-primary'" @click="setFilter('countryCode', '')">Visa alla</b-button>
                        <b-button v-for="countryCode in countryCodes"
                                  :variant="filter.countryCode === countryCode ? 'primary' : 'outline-primary'"
                                  :key="countryCode"
                                  @click="setFilter('countryCode', countryCode)">
                            {{ countryCode }}
                        </b-button>
                    </b-button-group>
                </b-card-header>
                <b-table
                    responsive=""
                    :items="dataProvider"
                    :fields="fields"
                    :filter="filter"
                    selectable
                    ref="parcelTable"
                    @row-selected="onRowSelected"
                >
                    <template #cell(service_type)="{item, value}">
                        {{ value }} ({{ item.destination_country }})
                    </template>
                    <template #cell(weight_spec)="{item: { price_spec } }">
                        <span v-b-tooltip.hover.html="getWeightTooltipHtml(price_spec)">{{ getWeight(price_spec) }}</span>
                    </template>
                    <template #cell(price)="{item, value}">
                        <span v-b-tooltip.hover.html="getSpecTooltipHtml(item)">{{ value }}</span>
                    </template>
                    <template #cell(details)="{item, toggleDetails, detailsShowing}">
                        <b-button variant="outline-primary" @click="toggleRowDetails(item, toggleDetails, detailsShowing)">Detaljer</b-button>
                    </template>
                    <template #head(checkbox)>
                        <b-form-checkbox @input="selectAllRows($event)"></b-form-checkbox>
                    </template>
                    <template #cell(reference)="{item, value}">
                        <span v-if="getReferenceUrl(item)">
                            <a :href="getReferenceUrl(item)">{{ value }}</a>
                            <a :href="getReferenceUrl(item)" target="_blank">
                                <i class="icon-new-tab"></i>
                            </a>
                        </span>
                        <span v-else>{{ item.reference }}</span>
                        <b-badge v-if="item.customer !== 'SUPEKORT-SE'" variant="warning">{{ item.customer }}</b-badge>
                    </template>
                    <template #cell(statustext)="{item, value}">
                        <a :href="item.tracking_link" target="_blank">{{ value }}</a>
                    </template>
                    <template #cell(checkbox)="{rowSelected}">
                        <b-form-checkbox class="disabled-but-enabled" :checked="rowSelected"></b-form-checkbox>
                    </template>
                    <template #cell(complaint)="{item: {id, service_type}}">
                        <span v-if="service_type && service_type.startsWith('PN-')">
                            <a :href="'/shipping/complaints/create/' + id">[REKL]</a>
                            <a :href="'/shipping/complaints/create/' + id" target="_blank">
                                <i class="icon-new-tab"></i>
                            </a>
                        </span>
                        <span v-else-if="service_type && service_type.startsWith('FDX-')">
                            FEDEX
                        </span>
                        <span v-else-if="service_type && service_type.startsWith('UPS-')">
                            UPS
                        </span>
                        <span v-else>
                            {{ service_type }}
                        </span>
                    </template>
                    <template #row-details="{item}">
                        <h5>Detaljer:</h5>
                        <b-row>
                            <b-col>
                                <b-card>
                                    <h6>Parcel:</h6>
                                    <dl>
                                        <dt>ID</dt>
                                        <dd>{{ item.parcel.id }}</dd>
                                        <dt>Shipment-ID</dt>
                                        <dd>{{ item.parcel.shipment_id }}</dd>
                                        <dt>Kollinummer</dt>
                                        <dd>{{ item.parcel.parcel_number }}</dd>
                                        <dt>Statuskod</dt>
                                        <dd>{{ item.parcel.statuscode }}</dd>
                                        <dt>Statustext</dt>
                                        <dd>{{ item.parcel.statustext }}</dd>
                                        <dt>EDI mottagen</dt>
                                        <dd>{{ item.parcel.edi_at }}</dd>
                                        <dt>Första scanning</dt>
                                        <dd>{{ item.parcel.first_scan_at }}</dd>
                                        <dt>Levererad</dt>
                                        <dd>{{ item.parcel.delivered_at }}</dd>
                                        <dt>Mottagen av</dt>
                                        <dd>{{ item.parcel.acceptor }}</dd>
                                        <dt>Prisimport datum</dt>
                                        <dd>{{ item.parcel.price_date }}</dd>
                                        <dt>Prisfakturanummer</dt>
                                        <dd>{{ item.parcel.price_invoice_no }}</dd>
                                        <dt>Pris</dt>
                                        <dd>{{ item.parcel.price }}</dd>
                                        <dt>Prisspecifikation</dt>
                                        <dd>{{ item.parcel.price_spec }}</dd>
                                        <dt>Pris godkänt</dt>
                                        <dd>{{ item.parcel.price_approved ? 'Ja' : 'Nej' }}</dd>
                                        <dt>Skapad</dt>
                                        <dd>{{ item.parcel.created_at }}</dd>
                                        <dt>Uppdaterad</dt>
                                        <dd>{{ item.parcel.updated_at }}</dd>
                                    </dl>
                                </b-card>
                            </b-col>
                            <b-col>
                                <b-card>
                                    <h6>Shipment:</h6>
                                    <dl>
                                        <dt>ID</dt>
                                        <dd>{{ item.parcel.shipment.id }}</dd>
                                        <dt>Jobb-ID</dt>
                                        <dd>{{ item.parcel.shipment.job_id }}</dd>
                                        <dt>Tillhör shipment med ID</dt>
                                        <dd>{{ item.parcel.shipment.belongs_to_shipment_id }}</dd>
                                        <dt>Externt ID</dt>
                                        <dd>{{ item.parcel.shipment.external_id }}</dd>
                                        <dt>Referens</dt>
                                        <dd>{{ item.parcel.shipment.reference }}</dd>
                                        <dt>Intern referens 1</dt>
                                        <dd>{{ item.parcel.shipment.internal_reference1 }}</dd>
                                        <dt>Intern referens 2</dt>
                                        <dd>{{ item.parcel.shipment.internal_reference2 }}</dd>
                                        <dt>Intern referens 3</dt>
                                        <dd>{{ item.parcel.shipment.internal_reference3 }}</dd>
                                        <dt>Kund</dt>
                                        <dd>{{ item.parcel.shipment.customer }}</dd>
                                        <dt>Tjänsteleverantör</dt>
                                        <dd>{{ item.parcel.shipment.service_provider }}</dd>
                                        <dt>Tjänst-typ</dt>
                                        <dd>{{ item.parcel.shipment.service_type }}</dd>
                                        <dt>Tjänst-addons</dt>
                                        <dd>{{ item.parcel.shipment.service_addons }}</dd>
                                        <dt>Mottagande postnummer</dt>
                                        <dd>{{ item.parcel.shipment.destination_postal_code }}</dd>
                                        <dt>Mottagande stad</dt>
                                        <dd>{{ item.parcel.shipment.destination_city }}</dd>
                                        <dt>Mottagande land</dt>
                                        <dd>{{ item.parcel.shipment.destination_country }}</dd>
                                        <dt>Notifierad</dt>
                                        <dd>{{ item.parcel.shipment.announced_at }}</dd>
                                        <dt>Skapad</dt>
                                        <dd>{{ item.parcel.shipment.created_at }}</dd>
                                        <dt>Uppdaterad</dt>
                                        <dd>{{ item.parcel.shipment.updated_at }}</dd>
                                        <dt>Raderad</dt>
                                        <dd>{{ item.parcel.shipment.deleted_at }}</dd>
                                        <dt>Errors</dt>
                                        <dd>{{ item.parcel.shipment.errors }}</dd>
                                    </dl>
                                </b-card>
                            </b-col>
                        </b-row>
                        <h5>Fakuradetaljer:</h5>
                        <div v-if="!item.invoiceDetails && !item.invoiceDetailsError">
                            <b-row>
                                <b-col>
                                    <marquee>Laddar...</marquee>
                                </b-col>
                                <b-col>
                                    <marquee direction="right">Laddar...</marquee>
                                </b-col>
                            </b-row>
                        </div>
                        <div v-else-if="item.invoiceDetailsError">
                            {{ item.invoiceDetailsError }}
                        </div>
                        <div v-else-if="!item.invoiceDetails.length">
                            Inga fakturadetaljer kunde hittas...
                        </div>
                        <div v-else>
                            <b-card v-for="details in item.invoiceDetails" :key="details.id">
                                <b-row>
                                    <b-col>
                                        <dl>
                                            <dt>ID</dt>
                                            <dd>{{ details.id }}</dd>
                                            <dt>Fakturaspecifikationsnummer</dt>
                                            <dd>{{ details.invoice_specification_number }}</dd>
                                            <dt>Fakturaskapandedatum</dt>
                                            <dd>{{ details.invoice_creation_date }}</dd>
                                            <dt>Kollinummer</dt>
                                            <dd>{{ details.parcel_number }}</dd>
                                            <dt>Referens</dt>
                                            <dd>{{ details.reference }}</dd>
                                            <dt>Första scanning</dt>
                                            <dd>{{ details.first_scan_date }}</dd>
                                            <dt>Beskrivning</dt>
                                            <dd>{{ details.description }}</dd>
                                        </dl>
                                    </b-col>
                                    <b-col>
                                        <dl>
                                            <dt>Produktkod</dt>
                                            <dd>{{ details.product_code }}</dd>
                                            <dt>Vikt</dt>
                                            <dd>{{ details.weight }}</dd>
                                            <dt>Volym</dt>
                                            <dd>{{ details.volume }}</dd>
                                            <dt>Fraktdragande vikt</dt>
                                            <dd>{{ details.freight_weight }}</dd>
                                            <dt>Mottagande postnummer</dt>
                                            <dd>{{ details.receiving_zip }}</dd>
                                            <dt>Mottagande stad</dt>
                                            <dd>{{ details.receiving_city }}</dd>
                                        </dl>
                                    </b-col>
                                    <b-col>
                                        <dl>
                                            <dt>Mottagande land</dt>
                                            <dd>{{ details.receiving_country }}</dd>
                                            <dt>Pris totalt</dt>
                                            <dd>{{ details.price_total }}</dd>
                                            <dt>Pris frakt</dt>
                                            <dd>{{ details.price_shipping }}</dd>
                                            <dt>Pris addons</dt>
                                            <dd>{{ details.price_addons }}</dd>
                                            <dt>Prisspecifikation</dt>
                                            <dd>{{ JSON.stringify(details.price_spec) }}</dd>
                                            <dt>Specifikation inläst</dt>
                                            <dd>{{ details.created_at }}</dd>
                                            <dt>Specifikation uppdaterad</dt>
                                            <dd>{{ details.updated_at }}</dd>
                                        </dl>
                                    </b-col>
                                </b-row>
                            </b-card>
                        </div>
                    </template>
                </b-table>
                <b-card-footer>
                    <div class="d-flex justify-content-end">
                        <limitless-button type="button" @click="approve" :busy="approving" icon="icon-checkmark-circle2">GODKÄNN</limitless-button>
                    </div>
                </b-card-footer>
            </b-card>
        </div>
    </div>
</template>

<script>

import ApiClient from '@services/ApiClient';
import LimitlessButton from '@components/Limitless/LimitlessButton.vue';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader.vue';

export default {
    components: {
        LimitlessButton,
        LimitlessPageHeader,
    },
    data() {
        return {
            loading: true,
            approving: false,
            serviceTypes: [],
            countryCodes: [],
            filter: {
                serviceType: '',
                countryCode: '',
            },
            selected: [],
            fields: [
                {
                    key: 'parcel_number',
                    label: 'Kollinummer',
                },
                {
                    key: 'reference',
                    label: 'Referens',
                },
                {
                    key: 'first_scan_at',
                    label: 'Första scanning',
                },
                {
                    key: 'service_type',
                    label: 'Benämning',
                },
                {
                    key: 'statustext',
                    label: 'Status',
                },
                {
                    key: 'weight_spec',
                    label: 'Vikt',
                },
                {
                    key: 'price',
                    label: 'Pris',
                    thClass: 'text-right',
                    tdClass: 'text-right',
                },
                {
                    key: 'details',
                    label: 'Detaljer',
                },
                {
                    key: 'checkbox',
                    label: 'Godkänn',
                },
                {
                    key: 'complaint',
                    label: '',
                },
            ],
        };
    },
    methods: {
        async dataProvider(ctx) {
            let url = '/vue/economy/shipping-cost-approval/list/' + ctx.filter.serviceType;
            if (ctx.filter.countryCode) {
                url += '?countryCode=' + ctx.filter.countryCode;
            }
            const response = await ApiClient.get(url)
                .catch((error) => error.response || { data: {}, status: 500 });

            if (this.filter.serviceType === '' && this.filter.countryCode === '') {
                this.serviceTypes = response.data.service_types ?? [];
                this.countryCodes = response.data.country_codes ?? [];
            }

            this.loading = false;

            return response.data.data ?? [];
        },
        setFilter(key, value) {
            this.loading = true;
            this.filter[key] = value;
        },
        onRowSelected(items) {
            this.selected = items.map((item) => item.id);
        },
        selectAllRows(event) {
            if (event === true) {
                this.$refs.parcelTable.selectAllRows();
                return;
            }
            this.$refs.parcelTable.clearSelected();
        },
        getReferenceUrl(item) {
            let url = null;
            switch (item.internal_reference1) {
            case 1:
                url = `/prodorders/${item.internal_reference2}`;
                break;
            case 2:
                url = `/returnorders/${item.internal_reference2}`;
                break;
            case 3:
                url = `/cardorders/${item.internal_reference2}`;
                break;
            case 4:
            default:
                break;
            }

            return url;
        },
        getWeight(priceSpec) {
            if (!priceSpec.weight && !priceSpec.freight_weight) {
                return '';
            }
            const weight = priceSpec.weight ?? 0;
            const freightWeight = priceSpec.freight_weight ?? 0;

            return Math.max(weight, freightWeight);
        },
        getWeightTooltipHtml(priceSpec) {
            if (!priceSpec.weight && !priceSpec.freight_weight) {
                return '';
            }

            const html = [];
            if (priceSpec.weight) {
                html.push(`Vikt: ${priceSpec.weight}`);
            }
            if (priceSpec.freight_weight) {
                html.push(`Fraktdragande vikt: ${priceSpec.freight_weight}`);
            }

            return html.join('<br/>');
        },
        getSpecTooltipHtml(item) {
            const html = [];
            if (item.price_spec?.price_shipping) {
                html.push(`Frakt: ${item.price_spec.price_shipping}`);
            }
            if (item.price_spec?.price_addons) {
                html.push(`Addons: ${item.price_spec.price_addons}`);
            }
            if (item.price_spec?.price_spec) {
                item.price_spec.price_spec.forEach((spec) => {
                    if (parseFloat(spec.price) !== 0) {
                        html.push(`${spec.description} (${spec.code}): ${spec.price}`);
                    }
                });
            }
            if (item.price_spec?.comment) {
                html.push(`Kommentar: ${item.price_spec.comment}`);
            }

            return html.join('<br/>');
        },
        toggleRowDetails(item, toggleDetails, detailsShowing) {
            if (!detailsShowing) {
                item.invoiceDetails = null;
                item.invoiceDetailsError = null;
                this.fetchDetails(item);
            }
            toggleDetails();
        },
        async fetchDetails(item) {
            if (!item.parcel_number) {
                item.invoiceDetailsError = 'Kollinummer saknas - inga detaljer kan hämtas.';
            }

            try {
                const response = await ApiClient.get(`/vue/economy/shipping-cost-approval/invoice-details/${item.parcel_number}`);
                item.invoiceDetails = response.data.data;
            } catch (error) {
                item.invoiceDetailsError = error?.response?.data?.message ?? 'Något gick fel när fakturadetaljerna skulle hämtas.';
            }
        },
        async approve() {
            this.approving = true;
            const count = this.selected.length;
            try {
                const payload = { ids: this.selected };
                await ApiClient.post('/vue/economy/shipping-cost-approval/approve', payload);
                this.$bvModal.msgBoxOk(`All done! ${count} fraktkostnader godkända.`, {
                    centered: true,
                    okTitle: 'OK',
                }).then(() => {
                    this.approving = false;
                    this.loading = true;
                    this.$refs.parcelTable.refresh();
                });
            } catch (error) {
                const errorMessage = error?.response?.data?.message ?? 'Okänt fel';
                this.$bvModal.msgBoxOk(`Något gick fel: försök igen! (${errorMessage})`, {
                    centered: true,
                    okTitle: 'OK',
                }).then(() => {
                    this.approving = false;
                    this.loading = true;
                    this.$refs.parcelTable.refresh();
                });
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.disabled-but-enabled {
    ::v-deep {
        .custom-control-label, .custom-control-input {
            pointer-events: none;
        }
    }
}
</style>
